import { useState } from 'react'
import { ApiError } from '../api.js'
import { isRole, roleHasCapability } from '@qim/shared'
import { formatInstant } from '../format.js'
import { ConfirmHide } from './confirm-hide.jsx'
import { RestoreIcon, TrashIcon } from './modal.jsx'
import type { JSX } from 'react'

/**
 * Hiding and restoring, wherever a list has rows someone may want gone.
 *
 * One component for customers, accounts and instances because it is one act —
 * the same reasoning that gave all three a single `records:manage` rather than
 * three capabilities. What differs between them is only what the confirmation
 * says, and whether there is one at all.
 */

/**
 * Whether to offer the control. Not a control — see the note on the button.
 *
 * Takes the raw string off `Me` and narrows with `isRole`, the convention the
 * other screens already use: a role the API knows and this build does not
 * should offer nothing rather than crash.
 */
export const canHide = (role: string): boolean =>
  isRole(role) && roleHasCapability(role, 'records:manage')

/**
 * Who hid this, and when.
 *
 * Attributed rather than merely greyed out, and for the same reason the access
 * screen shows a revoked assignment with its revocation attributed: a row that
 * has vanished with no account of who removed it is the gap IM-64 closed.
 *
 * A word, not a colour. This console gets read on a projector and screenshotted
 * into tickets.
 */
export const HiddenNote = ({
  at,
  by,
  status,
}: {
  readonly at: string
  readonly by?: string | undefined
  /** For an instance: the state it was in when someone hid it. */
  readonly status?: string | undefined
}): JSX.Element => (
  <p className="hidden-note">
    <span className="state state-hidden">Hidden</span>{' '}
    <span className="muted">
      {status === undefined ? '' : `while ${status}, `}
      {formatInstant(at)}
      {by === undefined ? '' : ` by ${by}`}
    </span>
  </p>
)

/**
 * The hide or restore button.
 *
 * **Not a control.** The API checks `records:manage` on every route and refuses
 * regardless; this only decides whether to *offer* it, because a button that
 * always fails is a worse way to say no than not offering one.
 *
 * `confirm` is set for a customer and an account and not for an instance, and
 * the asymmetry is deliberate — see IM-79. A customer is a whole tree and the
 * dialog is the only place that blast radius is stated; hiding one already
 * destroyed instance is trivially reversible, and gating it would train people
 * to dismiss the dialog, which is how a confirmation stops confirming anything.
 */
export const HideButton = ({
  role,
  hidden,
  what,
  name,
  confirm,
  onHide,
  onRestore,
  onChanged,
}: {
  readonly role: string
  readonly hidden: boolean
  /** 'customer' | 'account' | 'instance', for the dialog's wording. */
  readonly what: string
  readonly name: string
  readonly confirm: boolean
  readonly onHide: () => Promise<unknown>
  readonly onRestore: () => Promise<unknown>
  readonly onChanged: () => void
}): JSX.Element | null => {
  const [busy, setBusy] = useState(false)
  const [asking, setAsking] = useState(false)
  const [failure, setFailure] = useState<string | undefined>(undefined)

  if (!canHide(role)) return null

  const run = async (): Promise<void> => {
    setBusy(true)
    setFailure(undefined)
    try {
      await (hidden ? onRestore() : onHide())
      onChanged()
    } catch (error: unknown) {
      /*
       * The API's own reason, not "could not hide". A 409 says *what* is in the
       * way — "this customer still has instances that are live" — and that is
       * the difference between a fix in a minute and a support ticket.
       */
      const detail =
        error instanceof ApiError &&
        typeof (error.body as { detail?: unknown })?.detail === 'string'
          ? (error.body as { detail: string }).detail
          : 'Could not do that just now.'
      setFailure(detail)
    } finally {
      setBusy(false)
      setAsking(false)
    }
  }

  return (
    <div className="actions">
      {/*
        A trash icon and the danger tone, because that is what people look for
        when they want a row gone (IM-146) — and the word stays **Remove**
        rather than Delete.

        Destroy sits on these same screens and does reach a customer's real
        infrastructure, irreversibly. This removes a row from a list: the record
        survives, `records:manage` is deliberately not `deployment:destroy`, and
        the Lambda holds no `dynamodb:DeleteItem` at all, so "delete" would be
        the one word that is false about every part of it. The confirmation
        says so in as many words, and Restore is beside it afterwards.
      */}
      <button
        type="button"
        className={hidden ? 'ghost' : 'ghost danger'}
        disabled={busy}
        onClick={() => {
          if (hidden || !confirm) void run()
          else setAsking(true)
        }}
      >
        {hidden ? <RestoreIcon /> : <TrashIcon />}
        {busy ? 'Working…' : hidden ? 'Restore' : 'Remove'}
      </button>
      {failure === undefined ? null : <span className="failure">{failure}</span>}
      {asking ? (
        <ConfirmHide
          what={what}
          name={name}
          onConfirm={() => void run()}
          onCancel={() => setAsking(false)}
        />
      ) : null}
    </div>
  )
}
